import * as Ariakit from "@ariakit/react";
import { CaretDown, CaretUp, Plus } from "@phosphor-icons/react";
import { Button } from "@replicate/ui";
import { useMemo, useState } from "react";
import { Remark } from "react-remark";
import type { OperationDefinition } from ".";
import { supportsStreaming } from "../../code-snippets";
import type { Version } from "../../types";
import type { RunWithContext } from "../api-playground/run-with";

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <span className="uppercase text-r8-gray-12 text-r8-xs font-semibold tracking-wider">
    {children}
  </span>
);

export interface OperationExample {
  description: string;
  label: string;
  snippets: {
    javascript: string | React.ReactNode;
    python: string | React.ReactNode;
    bash: string | React.ReactNode;
  };
}

interface RequestBodyParameter {
  description: string;
  type: string;
}

interface RequestBodySchema {
  properties: Record<string, RequestBodyParameter>;
  required: string[];
  type: "object";
}

export function EndpointDocs({
  examples = [],
  language,
  operation,
  usesVersionlessApi,
  version,
}: {
  examples: (OperationExample | null)[];
  language: RunWithContext;
  operation: OperationDefinition;
  usesVersionlessApi: boolean;
  version: Version;
}) {
  const filteredExamples = examples.filter(
    (example): example is OperationExample => Boolean(example)
  );
  const [open, setOpen] = useState(true);
  const [examplesOpenMap, setExamplesOpenMap] = useState<Array<boolean>>(() => {
    return filteredExamples.map((_, i) => {
      if (i === 0) return true;
      return false;
    });
  });

  const activeExampleIndex = examplesOpenMap.findIndex((open) => open);
  const activeExample = filteredExamples[activeExampleIndex];

  const { parameters } = operation;

  const inputParameters = parameters.filter((p) => p.in === "path");
  const headers = parameters.filter((p) => p.in === "header");

  const CurlComponent = activeExample.snippets.bash;
  const PythonComponent = activeExample.snippets.python;
  const JavascriptComponent = activeExample.snippets.javascript;

  const codeSnippet: React.ReactNode = {
    http: CurlComponent,
    python: PythonComponent,
    nodejs: JavascriptComponent,
  }[language];

  const requestBody = operation.request_body?.content?.["application/json"]
    ?.schema as RequestBodySchema;

  const maybeFilteredRequestBody = useMemo(() => {
    if (operation.slug !== "predictions.create") {
      return requestBody;
    }

    let filteredProperties = { ...requestBody.properties };

    if (usesVersionlessApi) {
      // Remove the `version` parameter from the docs.
      const { version, ...properties } = filteredProperties;
      filteredProperties = properties;
    }

    if (supportsStreaming(version._extras) === "none") {
      // Remove the `stream` parameter from the docs.
      const { stream, ...properties } = filteredProperties;
      filteredProperties = properties;
    }

    return { ...requestBody, properties: filteredProperties };
  }, [operation, requestBody, usesVersionlessApi, version]);

  return (
    <Ariakit.DisclosureProvider open={open} setOpen={setOpen}>
      <Ariakit.Disclosure
        className={`py-10 border-b ${
          open ? "border-transparent" : "border-r8-gray-6"
        }`}
      >
        <div className="flex items-center justify-between">
          <p className="text-r8-xl">{operation.summary}</p>
          <div className="flex items-center gap-2">
            <span className="font-mono text-r8-sm font-semibold text-r8-gray-11">
              {operation.slug}
            </span>
            <span className="flex-shrink-0">
              {open ? <CaretUp /> : <CaretDown />}
            </span>
          </div>
        </div>
      </Ariakit.Disclosure>
      <Ariakit.DisclosureContent className="pt-0 pb-10">
        <div className="gap-4 flex flex-col">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="min-w-0 space-y-4">
              {headers.length > 0 ? (
                <section>
                  <div className="mb-2">
                    <SectionHeading>Headers</SectionHeading>
                  </div>
                  <ul className="border-t border-r8-gray-6">
                    {headers.map((header) => {
                      return (
                        <li key={header.name} className="py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-r8-sm">
                                {header.name}
                              </span>
                              <span className="font-mono text-r8-xs px-1 py-0.5 bg-r8-gray-3 font-semibold">
                                {header.schema.type}
                              </span>
                            </div>
                          </div>
                          {header.description ? (
                            <div className="mt-1">
                              <TruncatedDescription>
                                {header.description}
                              </TruncatedDescription>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ) : null}
              {inputParameters.length > 0 ? (
                <section>
                  <div className="mb-2">
                    <SectionHeading>Input parameters</SectionHeading>
                  </div>
                  <ul className="border-t border-r8-gray-6">
                    {parameters.map((parameter) => {
                      return (
                        <li key={parameter.name} className="py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-r8-sm">
                                {parameter.name}
                              </span>
                              <span className="font-mono text-r8-xs px-1 py-0.5 bg-r8-gray-3 font-semibold">
                                {parameter.schema.type}
                              </span>
                              {parameter.required ? (
                                <span className="font-mono text-r8-xs px-1 py-0.5 bg-r8-red-3 text-r8-red-11 font-semibold">
                                  Required
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="text-r8-gray-12 text-r8-sm mt-1">
                            {parameter.description}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ) : null}
              {maybeFilteredRequestBody ? (
                <section>
                  <div className="mb-2">
                    <SectionHeading>Request body</SectionHeading>
                  </div>
                  <ul className="border-t border-r8-gray-6">
                    {Object.keys(maybeFilteredRequestBody.properties).map(
                      (name) => {
                        const property =
                          maybeFilteredRequestBody.properties[name];
                        return (
                          <li key={name} className="py-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-r8-sm">
                                  {name}
                                </span>
                                <span className="font-mono text-r8-xs px-1 py-0.5 bg-r8-gray-3 font-semibold">
                                  {property.type}
                                </span>
                                {maybeFilteredRequestBody.required.includes(
                                  name
                                ) ? (
                                  <span className="font-mono text-r8-xs px-1 py-0.5 bg-r8-red-3 text-r8-red-11 font-semibold">
                                    Required
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="mt-1">
                              <TruncatedDescription>
                                {property.description}
                              </TruncatedDescription>
                            </div>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </section>
              ) : null}
              <section>
                <div className="mb-2">
                  <SectionHeading>Examples</SectionHeading>
                </div>
                <div className="border-t border-r8-gray-6 flex flex-col">
                  {filteredExamples.map((example, index) => {
                    const isOpen = examplesOpenMap[index];
                    return (
                      <Ariakit.DisclosureProvider
                        open={isOpen}
                        setOpen={() => {
                          setExamplesOpenMap((prev) => {
                            return prev.map((_, i) => i === index);
                          });
                        }}
                        key={`example-${index}`}
                      >
                        <Ariakit.Disclosure
                          className={`${
                            !isOpen
                              ? "border-r8-gray-6 opacity-70 bg-r8-gray-2 hover:bg-r8-gray-1"
                              : "border-transparent font-semibold cursor-default"
                          } text-left p-2 text-base border-b flex items-center justify-between`}
                        >
                          <div className="flex-1">{example.label}</div>
                          <span className="text-r8-gray-11">
                            {isOpen ? null : <Plus weight="bold" />}
                          </span>
                        </Ariakit.Disclosure>
                        <Ariakit.DisclosureContent
                          className={`${
                            isOpen ? "border-r8-gray-6" : "border-transparent"
                          } border-b px-2 pt-2 pb-3 -mt-4 space-y-2`}
                        >
                          <p className="text-r8-gray-12 text-sm">
                            {example.description}
                          </p>
                        </Ariakit.DisclosureContent>
                      </Ariakit.DisclosureProvider>
                    );
                  })}
                </div>
              </section>
            </div>
            <div className="min-w-0">
              <section className="sticky top-16">
                <div className="mb-2">
                  <SectionHeading>Make a request</SectionHeading>
                </div>
                <div className="border border-r8-gray-6">
                  <div className="bg-r8-gray-1 p-2 gap-2 flex items-center overflow-auto">
                    <span className="bg-r8-blue-6 text-r8-blue-11 py-0.5 px-2 text-r8-xs font-bold font-mono uppercase select-none pointer-events-none">
                      {operation.method}
                    </span>
                    <span className="text-r8-gray-12 text-r8-sm font-mono">
                      {operation.path}
                    </span>
                  </div>
                  <div>{codeSnippet}</div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </Ariakit.DisclosureContent>
    </Ariakit.DisclosureProvider>
  );
}

function TruncatedDescription({ children }) {
  const [open, setOpen] = useState(false);
  const hasEnoughLines = children.split("\n").length >= 2;

  return (
    <>
      <div className={`${open ? "line-clamp-none" : "line-clamp-2"}`}>
        <div className="prose prose-sm dark:prose-invert">
          <Remark>{children}</Remark>
        </div>
      </div>
      {hasEnoughLines ? (
        <div className="mt-2">
          <Button
            onClick={() => {
              setOpen((prev) => !prev);
            }}
            variant="outlined"
            size="xs"
          >
            {open ? "Show less" : "Show more"}
          </Button>
        </div>
      ) : null}
    </>
  );
}
